import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Button,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import EditIcon from '../../../assets/icons/EditBlue.svg';
import { ReactComponent as EyeOffIcon } from '../../../assets/icons/EyeOff.svg';
import AntSwitch from '../../UIControls/AntSwitch';
import PinkGradientButton from '../../UIControls/PinkGradientButton';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import UserApi from '../../../rest-api/user-api';

const helpLinkSx = {
    color: '#FF669B',
    p: 0,
    minWidth: 0,
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: '14px',
    letterSpacing: 0,
    textDecoration: 'underline',
    textTransform: 'none',
    '&:hover': {
        background: 'transparent',
        textDecoration: 'underline',
    },
};

const helpDialogTextSx = {
    color: '#616063',
    fontFamily: 'Inter',
    fontSize: { xs: 14, sm: 18 },
    lineHeight: { xs: '18px', sm: '26px' },
    letterSpacing: 0,
    mb: 2,
};

const SettingsIntegrations = ({
    clinicianData,
    idToken,
    onRefresh,
    isFreeplan = false,
    onUpgradeClick = () => {},
    isMultiUser = false,
    inviteId = null,
    emailAddress = '',
    currentUserId = '',
}) => {
    const [editorActive, setEditorActive] = useState(false);
    const [integrationEnabled, setIntegrationEnabled] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);
    const [status, setStatus] = useState({ state: 'idle', message: 'Not connected' });
    const [statusMessage, setStatusMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);

    const existingIntegration = useMemo(() => {
        if (!clinicianData) return null;
        if (isMultiUser) {
            const multiuser = clinicianData.multiuserData?.multiuserUsers?.find(
                (user) => user.userId === currentUserId,
            );
            return multiuser?.integrations?.cliniko || null;
        }
        return clinicianData.integrations?.cliniko || null;
    }, [clinicianData, isMultiUser, currentUserId]);

    const storedApiKey = existingIntegration?.apiKey || '';
    const hasSavedKey = Boolean(storedApiKey);

    useEffect(() => {
        setIntegrationEnabled(hasSavedKey);
        setApiKey('');
        setShowApiKey(false);
    }, [hasSavedKey]);

    useEffect(() => {
        let isActive = true;
        const storedKey = existingIntegration?.apiKey;

        if (!storedKey) {
            setStatus({ state: 'disconnected', message: 'Not connected' });
            return () => {
                isActive = false;
            };
        }

        if (!idToken) {
            setStatus({ state: 'checking', message: 'Checking connection...' });
            return () => {
                isActive = false;
            };
        }

        setStatus({ state: 'checking', message: 'Checking connection...' });
        UserApi.validateClinikoKey(idToken, storedKey)
            .then((response) => {
                if (!isActive) return;
                if (response?.valid) {
                    setStatus({ state: 'connected', message: 'Connected' });
                } else {
                    setStatus({ state: 'disconnected', message: 'Not connected' });
                }
            })
            .catch(() => {
                if (!isActive) return;
                setStatus({ state: 'error', message: 'Unable to validate connection' });
            });

        return () => {
            isActive = false;
        };
    }, [existingIntegration?.apiKey, idToken]);

    const handleCloseEdit = () => {
        setApiKey('');
        setShowApiKey(false);
        setIntegrationEnabled(hasSavedKey);
        setStatusMessage('');
    };

    const handleToggleEdit = () => {
        if (isFreeplan) {
            onUpgradeClick();
            return;
        }

        if (editorActive) {
            handleCloseEdit();
            setEditorActive(false);
            return;
        }

        setEditorActive(true);
        setApiKey(storedApiKey);
        setShowApiKey(false);
        if (!hasSavedKey) {
            setIntegrationEnabled(true);
        }
    };

    const handleToggleIntegration = (event) => {
        if (isFreeplan) {
            onUpgradeClick();
            return;
        }

        const checked = event.target.checked;
        if (!checked && hasSavedKey) {
            setRemoveDialogOpen(true);
            return;
        }

        setIntegrationEnabled(checked);
        if (checked) {
            setEditorActive(true);
            setApiKey(storedApiKey);
            setShowApiKey(false);
        }
        if (!checked) {
            setApiKey('');
            setStatusMessage('');
            setEditorActive(false);
        }
    };

    const handleConnect = async () => {
        if (isFreeplan) {
            onUpgradeClick();
            return;
        }

        if (!idToken) {
            setStatusMessage('Please wait while your session loads.');
            return;
        }

        const trimmedKey = apiKey.trim();
        if (!trimmedKey) {
            setStatusMessage('Please paste your Cliniko API key.');
            return;
        }

        setIsSubmitting(true);
        setStatusMessage('');
        try {
            const validationResponse = await UserApi.validateClinikoKey(idToken, trimmedKey);
            if (!validationResponse?.valid) {
                setStatus({ state: 'disconnected', message: 'Not connected' });
                setStatusMessage('Cliniko API key is invalid.');
                return;
            }

            const payload = {
                integrations: {
                    cliniko: {
                        apiKey: trimmedKey,
                        userId: validationResponse.userId || null,
                        shard: validationResponse.shard || null,
                    },
                },
            };

            if (isMultiUser) {
                await UserApi.updateClinicianDataMultiuser(idToken, {
                    ...payload,
                    action: 'updateMultiuser',
                    inviteId,
                    emailAddress,
                });
            } else {
                await UserApi.updateClinicianData(idToken, payload);
            }

            setStatus({ state: 'connected', message: 'Connected' });
            setIntegrationEnabled(true);
            setApiKey('');
            setShowApiKey(false);
            setEditorActive(false);
            if (onRefresh) {
                await onRefresh();
            }
        } catch (error) {
            if (error?.response?.status === 403) {
                onUpgradeClick();
            } else {
                setStatusMessage(error?.message || 'Unable to connect Cliniko.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveConfirmed = async () => {
        setRemoveDialogOpen(false);
        setIsSubmitting(true);
        setStatusMessage('');
        try {
            if (!idToken) {
                throw new Error('Please wait while your session loads.');
            }

            const payload = {
                integrations: {
                    cliniko: {
                        apiKey: '',
                    },
                },
            };

            if (isMultiUser) {
                await UserApi.updateClinicianDataMultiuser(idToken, {
                    ...payload,
                    action: 'updateMultiuser',
                    inviteId,
                    emailAddress,
                });
            } else {
                await UserApi.updateClinicianData(idToken, payload);
            }

            setApiKey('');
            setIntegrationEnabled(false);
            setStatus({ state: 'disconnected', message: 'Not connected' });
            setEditorActive(false);
            if (onRefresh) {
                await onRefresh();
            }
        } catch (error) {
            setStatusMessage(error?.message || 'Unable to remove Cliniko integration.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const statusColor =
        status.state === 'connected'
            ? '#00C500'
            : status.state === 'error'
              ? '#C62828'
              : status.state === 'checking'
                ? '#6b6b6b'
                : '#616063';
    const trimmedApiKey = apiKey.trim();
    const canConnect =
        editorActive &&
        integrationEnabled &&
        Boolean(trimmedApiKey) &&
        trimmedApiKey !== storedApiKey &&
        Boolean(idToken) &&
        !isSubmitting;
    const showStatus = integrationEnabled || hasSavedKey;
    const showRemoveButton = editorActive && integrationEnabled && hasSavedKey;
    const showApiKeyToggle = Boolean(apiKey) || hasSavedKey;

    return (
        <Box
            className="settings-block"
            sx={{
                backgroundColor: 'white',
                borderRadius: '30px',
                padding: { xs: '20px', sm: '24px' },
                my: 3,
                boxSizing: 'border-box',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Typography
                    variant="h5"
                    sx={{
                        color: '#000453',
                        fontFamily: 'Inter',
                        fontSize: { xs: 26, sm: 30 },
                        fontWeight: 600,
                        lineHeight: '30px',
                        letterSpacing: 0,
                    }}
                >
                    Cliniko Integration
                </Typography>
                <IconButton
                    onClick={handleToggleEdit}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                    sx={{
                        p: 0,
                        width: 38,
                        height: 38,
                        borderRadius: '38px',
                        bgcolor: editorActive ? '#000453' : '#FF669B',
                        '&:hover': { bgcolor: editorActive ? '#1C2A8A' : '#FF85B0' },
                        '&:active': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                        '&:focus': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                        '&.Mui-focusVisible': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                        WebkitTapHighlightColor: 'transparent',
                        flex: '0 0 auto',
                    }}
                >
                    <img src={EditIcon} alt="Edit" style={{ width: 18, height: 18 }} />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 2 }}>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#616063',
                        fontFamily: 'Inter',
                        fontSize: { xs: 18, sm: 22 },
                        fontWeight: 400,
                        lineHeight: { xs: '22px', sm: '22px' },
                        letterSpacing: 0,
                    }}
                >
                    Enable Cliniko Integration
                </Typography>
                <AntSwitch checked={integrationEnabled} onChange={handleToggleIntegration} />
            </Box>

            {editorActive && integrationEnabled && (
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#616063',
                            fontFamily: 'Inter',
                            fontSize: 14,
                            lineHeight: '14px',
                            letterSpacing: 0,
                            mb: 1,
                        }}
                    >
                        Please paste the copied Cliniko API key below
                    </Typography>
                    <TextField
                        fullWidth
                        type={showApiKey ? 'text' : 'password'}
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        InputProps={{
                            endAdornment: showApiKeyToggle ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowApiKey((prev) => !prev)}
                                        edge="end"
                                        aria-label={showApiKey ? 'Hide Cliniko API key' : 'Show Cliniko API key'}
                                        disableRipple
                                    >
                                        <EyeOffIcon style={{ width: 19, height: 16, opacity: showApiKey ? 0.5 : 1 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                height: 43,
                                borderRadius: '9px',
                                background: '#FFFFFF',
                            },
                            '& .MuiInputBase-input': {
                                height: 43,
                                boxSizing: 'border-box',
                                py: 0,
                                fontFamily: 'Inter',
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'rgba(102, 102, 102, 0.35)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(102, 102, 102, 0.5)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#FF669B',
                                    borderWidth: 1,
                                },
                            },
                        }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            mt: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button variant="text" onClick={() => setHelpDialogOpen(true)} sx={helpLinkSx}>
                            How to find your Cliniko API key
                        </Button>
                        {showStatus && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: statusColor,
                                    fontFamily: 'Inter',
                                    fontSize: 14,
                                    fontWeight: status.state === 'connected' ? 500 : 400,
                                    lineHeight: '14px',
                                    letterSpacing: 0,
                                }}
                            >
                                {status.state === 'checking' ? 'Checking connection...' : status.message}
                            </Typography>
                        )}
                    </Box>

                    {statusMessage && (
                        <Typography variant="body2" sx={{ color: '#C62828', mt: 1, fontFamily: 'Inter' }}>
                            {statusMessage}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: showRemoveButton ? '1fr 1fr' : '1fr' },
                            gap: { xs: 1.5, sm: 2.5 },
                            mt: 3,
                        }}
                    >
                        <PinkGradientButton
                            onClick={handleConnect}
                            disabled={!canConnect}
                            sx={{
                                width: '100%',
                                height: 44,
                                m: 0,
                                borderRadius: '200px',
                                fontFamily: 'Inter',
                                fontSize: 18,
                                fontWeight: 500,
                                lineHeight: '18px',
                                letterSpacing: 0,
                                textTransform: 'none',
                                backgroundColor: !canConnect ? '#E5E5E5' : undefined,
                                color: '#FFFFFF',
                                '&.Mui-disabled': {
                                    backgroundColor: '#E5E5E5',
                                    color: '#FFFFFF',
                                },
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Connect'}
                        </PinkGradientButton>
                        {showRemoveButton && (
                            <Button
                                variant="outlined"
                                onClick={() => setRemoveDialogOpen(true)}
                                disabled={isSubmitting}
                                sx={{
                                    width: '100%',
                                    height: 44,
                                    borderRadius: '200px',
                                    borderColor: '#DF0000',
                                    color: '#DF0000',
                                    fontFamily: 'Inter',
                                    fontSize: 18,
                                    fontWeight: 500,
                                    lineHeight: '18px',
                                    letterSpacing: 0,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: '#DF0000',
                                        backgroundColor: 'rgba(223, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                Remove
                            </Button>
                        )}
                    </Box>
                </Box>
            )}

            {(!editorActive || !integrationEnabled) && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            mt: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button variant="text" onClick={() => setHelpDialogOpen(true)} sx={helpLinkSx}>
                            How to find your Cliniko API key
                        </Button>
                        {showStatus && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: statusColor,
                                    fontFamily: 'Inter',
                                    fontSize: 14,
                                    fontWeight: status.state === 'connected' ? 500 : 400,
                                    lineHeight: '14px',
                                    letterSpacing: 0,
                                }}
                            >
                                {status.state === 'checking' ? 'Checking connection...' : status.message}
                            </Typography>
                        )}
                    </Box>
                    {statusMessage && (
                        <Typography variant="body2" sx={{ color: '#C62828', mt: 1, fontFamily: 'Inter' }}>
                            {statusMessage}
                        </Typography>
                    )}
                </>
            )}

            <Dialog
                open={helpDialogOpen}
                onClose={() => setHelpDialogOpen(false)}
                PaperProps={{
                    className: 'dialog-container',
                    sx: {
                        p: { xs: 2.5, sm: 4 },
                        minWidth: { xs: 'auto', sm: '500px' },
                        maxWidth: '711px',
                        borderRadius: 4,
                        width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                        m: { xs: 2, sm: 4 },
                    },
                }}
            >
                <DialogTitle
                    variant="h4"
                    sx={{
                        p: 0,
                        color: '#000453',
                        fontFamily: 'Inter',
                        fontSize: { xs: 26, sm: 32 },
                        fontWeight: 600,
                        lineHeight: { xs: '26px', sm: '32px' },
                        letterSpacing: 0,
                    }}
                >
                    Cliniko integration setup
                </DialogTitle>
                <DialogContent sx={{ p: 0, mt: 3, mb: 4 }}>
                    <Typography variant="body1" sx={helpDialogTextSx}>
                        Step 1: Log in to your Cliniko account. Click <strong>My info</strong> in the lower-left corner,
                        then scroll down to <strong>Manage API keys</strong>.
                    </Typography>
                    <Typography variant="body1" sx={helpDialogTextSx}>
                        Step 2: Click <strong>Add an API key</strong>. Give it a name (e.g., "Cetra Health") and press{' '}
                        <strong>Create API key</strong>.
                    </Typography>
                    <Typography variant="body1" sx={helpDialogTextSx}>
                        Step 3: Copy the new API key. Paste it into the Cliniko integration field here and press{' '}
                        <strong>Connect</strong>.
                    </Typography>
                    <Typography variant="body1" sx={{ ...helpDialogTextSx, mb: 0 }}>
                        Note: Each practitioner should create their own API key.
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        p: 0,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <PinkGradientButton
                        onClick={() => setHelpDialogOpen(false)}
                        sx={{
                            width: { xs: '100%', sm: 160 },
                            height: 48,
                            background: '#FF669B',
                            backgroundImage: 'none',
                            '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                            color: '#FFFFFF',
                        }}
                    >
                        Close
                    </PinkGradientButton>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                open={removeDialogOpen}
                handleClose={() => setRemoveDialogOpen(false)}
                onConfirm={handleRemoveConfirmed}
                title="Remove Cliniko integration?"
                message="Are you sure you want to remove your Cliniko API key from Cetra?"
                confirmText="Remove"
                cancelText="Cancel"
            />
        </Box>
    );
};

export default SettingsIntegrations;
